import { z } from "zod";
import {
  BRIEFING_KINDS,
  CATEGORIES,
  CIRCLE_KINDS,
  OPPORTUNITY_KINDS,
  PILLARS,
  ROLES,
} from "./constants.js";

/** Onboarding payload — used by frontend + API. */
export const OnboardingPayload = z.object({
  displayName: z.string().min(1).max(80),
  primaryCommunityId: z.string().min(1),
  city: z.string().min(1).max(80).optional(),
  region: z.string().max(80).optional(),
  country: z.string().max(80).optional(),
  goals: z.array(z.string().min(1).max(120)).min(1).max(8),
  identityTags: z.array(z.string().min(1).max(60)).max(12).optional(),
  pillars: z.object({
    mind: z.number().min(0).max(1),
    body: z.number().min(0).max(1),
    soul: z.number().min(0).max(1),
    lifestyle: z.number().min(0).max(1),
  }),
  interests: z.array(z.enum(CATEGORIES)).min(1).max(CATEGORIES.length),
  faithPreference: z.string().max(60).optional(),
  reflection: z.string().max(2000).optional(),
});
export type OnboardingPayload = z.infer<typeof OnboardingPayload>;

/** Strict shape we expect back from OpenAI for each ingested item. */
export const AiSynthesisOutput = z.object({
  summary: z.string().min(1),
  why_it_matters: z.string().min(1),
  emotional_tone: z.string().nullable(),
  opportunity_score: z.number().min(0).max(1),
  urgency_score: z.number().min(0).max(1),
  trend_score: z.number().min(0).max(1),
  relevance_score: z.number().min(0).max(1),
  leadership_relevance: z.number().min(0).max(1),
  sentiment: z.enum(["positive", "neutral", "negative", "mixed"]),
  audience_tags: z.array(z.string()).max(20),
  geo_tags: z.array(z.string()).max(20),
  category_tags: z.array(z.enum(CATEGORIES)).max(CATEGORIES.length),
  opportunities: z
    .array(
      z.object({
        kind: z.enum(OPPORTUNITY_KINDS),
        title: z.string(),
        url: z.string().url().optional(),
        deadline_iso: z.string().optional(),
        amount_usd: z.number().nonnegative().optional(),
      }),
    )
    .max(10),
  risks: z.array(z.string()).max(10),
  pillars: z.object({
    mind: z.number().min(0).max(1),
    body: z.number().min(0).max(1),
    soul: z.number().min(0).max(1),
    lifestyle: z.number().min(0).max(1),
  }),
});
export type AiSynthesisOutput = z.infer<typeof AiSynthesisOutput>;

export const BriefingRequest = z.object({
  kind: z.enum(BRIEFING_KINDS),
  windowHours: z.number().int().min(1).max(168).default(24),
  geo: z.string().max(80).optional(),
  pillarFocus: z.enum(PILLARS).optional(),
});
export type BriefingRequest = z.infer<typeof BriefingRequest>;

/**
 * Structured briefing — what the AI returns now. Replaces the previous
 * free-form markdown. Renders via a typed component on the client; no
 * markdown parser involved. All string fields are plain prose (the prompt
 * is explicit that no markdown syntax may appear in any value).
 *
 * Optional sections are omitted by the model when there's nothing to say;
 * the renderer hides empty sections rather than showing placeholder copy.
 */
export const BriefingStructured = z.object({
  read: z.string().min(1).max(2000),
  signals: z
    .array(
      z.object({
        headline: z.string().min(1).max(400),
        context: z.string().max(400).optional(),
        source_name: z.string().max(120).optional(),
      }),
    )
    .max(8),
  opportunities: z
    .array(
      z.object({
        title: z.string().min(1).max(200),
        detail: z.string().max(400).optional(),
        deadline: z.string().max(40).optional(),
      }),
    )
    .max(6)
    .optional(),
  watchlist: z.array(z.string().min(1).max(300)).max(5).optional(),
  reflection: z.string().max(400).optional(),
});
export type BriefingStructured = z.infer<typeof BriefingStructured>;

export const CreateCircleInput = z.object({
  communityId: z.string().min(1),
  name: z.string().min(2).max(80),
  kind: z.enum(CIRCLE_KINDS),
  description: z.string().max(500).optional(),
  cadence: z.enum(["weekly", "biweekly", "monthly"]).optional(),
  isPrivate: z.boolean().default(true),
});

export const StripeCheckoutInput = z.object({
  planId: z.string().min(1),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});

export const SetRoleInput = z.object({
  userId: z.string().min(1),
  role: z.enum(ROLES),
});
